import AddressSearcherApi from "./control/AddressSearcherApi";
import AIModule from "./module/AIModule";
import AjaxRequestBroker from "./AjaxRequestBroker"
import AnalysisModule from "./module/AnalysisModule";
import AppResourcesModule from "./module/AppResourcesModule";
import AsyncTasksModule from "./module/AsyncTasksModule";
import BufferModule from "./module/BufferModule";
import CacheModule from "./module/CacheModule";
import DataStoreModule from "./module/DataStoreModule";
import DistanceModule from "./module/DistanceModule";
import ExportModule from "./module/ExportModule";
import FeatureModule from "./module/FeatureModule";
import GeocodeModule from "./module/GeocodeModule";
import GeoMapTemplate from "./module/GeoMapTemplateModule";
import GeomModule from "./module/GeomModule";
import GeoMapAnnotationModule from "./module/GeoMapAnnotationModule";
import GeoShapeModule from "./module/GeoShapeModule";
import HeuristicsModule from "./module/HeuristicsModule";
import ImportModule from "./module/ImportModule";
import IntegrationSalesforceModule from "./module/IntegrationSalesforceModule";
import LayerInfoModule from "./module/LayerInfoModule";
import LayerViewModule from "./module/LayerViewModule";
import LayerMetaModule from "./module/LayerMetaModule";
import NotificationModule from "./module/NotificationModule";
import ProjectionModule from "./module/ProjectionModule";
import PredefinedRangesModule from "./module/PredefinedRangesModule";
import RouteModule from "./module/RouteModule";
import ScheduleModule from "./module/ScheduleModule";
import ScheduledUpdateModule from "./module/ScheduledUpdateModule";
import SearchModule from "./module/SearchModule";
import SecurityModule from "./module/SecurityModule";
import StatusModule from "./module/StatusModule";
import StyleModule from "./module/StyleModule";
import SymbolTypeModule from "./module/SymbolTypeModule";
import TenantModule from "./module/TenantModule";
import TenantAdminModule from "./module/TenantAdminModule";
import TerritoryModule from "./module/TerritoryModule";
import ThematicModule from "./module/ThematicModule";
import UploadModule from "./module/UploadModule";
import UserSettingsModule from "./module/UserSettingsModule";
import GeoMapModule from "./module/GeoMapModule";
import BucketModule from "./module/BucketModule";
import LoggingModule from "./module/LoggingModule";
import MCPModule from "./module/MCPModule";
import AppUserSupportModule from "./module/AppUserSupportModule";
import WebhookModule from "./module/WebhookModule";
import WebSocketModule from "./module/WebSocketModule";
import WorkspaceModule from "./module/WorkspaceModule";

import type { Criteria } from "./model/Criteria";
import type { SortOrder } from "./model/Feature";

const es = global.es; // TODO Remove dependency!

// TODO: root out events that don't work on new api
const EVENTS = {
    onSessionTimeout: 1,
    onSessionTimeoutWarn: 1,
    onRememberMeSessionRecovery: 1,
    onInit: 1,
    onDestroy: 1,
    onLoadStart: 1,
    onLoadFinish: 1,
    onUpdateProfile: 1,
    /* Usually not on the profile request but are added in the home */
    onLoadMapList: 1,
    onLoadQueryList: 1,
    onLoadGeomap: 1,
    onLoadWorkspace: 1,
    onLoadTenant: 1,
    onLoadUserSettings: 1,

    onExecuteRqFail: 1,

    onCreateWorkspace : 1,
    onUpdateWorkspace : 1,
    onRemoveWorkspace : 1,

    onCreateGeoMap : 1,
    onBeforeUpdateGeomap: 1,
    onUpdateGeoMap : 1,
    onCloneGeoMap : 1,
    onRemoveGeoMap : 1,

    onCreateGeoMapTemplate : 1,
    onRemoveGeoMapTemplate : 1,

    onCreateGeoMapAnnotation : 1,
    onUpdateGeoMapAnnotation : 1,
    onRemoveGeoMapAnnotation : 1,

    onCreateLayerMeta : 1,
    onUpdateLayerMeta : 1,
    onRemoveLayerMeta : 1,
    
    onCreateLayerView : 1,
    onBeforeUpdateLayerView : 1,
    onUpdateLayerView : 1,
    onRemoveLayerView : 1,
    onUpdateLayerViewRemovableCriteria : 1,

    onGetLayerInfo : 1,

    onRemoveFeature : 1,
    onCreateFeature : 1,
    onBatchEditFeatures: 1,
    onUpdateFeature : 1,
    onEditFeatures : 1,
    
    onMultiSelectFeatures : 1,

    onSyncDatasetStart : 1,
    onSyncDatasetFinish : 1, // see also: onSyncDatasetFinishAndFeatureViewModelUpdated

    onCreateAnalysis : 1,
    onUpdateAnalysis : 1,
    onRevertAnalysis : 1,

    onGetDefaultStyler : 1,

    onStaleResource : 1,

    onImportDatasetByURL : 1,

    onCreateTerritory : 1,
    onUpdateThematic : 1,

    onGetRoute : 1,
    onRemoveRoute : 1,

    onEditTerritories: 1,
    onExitEditTerritories: 1,

    onEditTerritoryAttribute: 1,
    onEditTerritoryDerivedAttribute:1,
    onEditTerritoryLevelLabels:1,

    onCalculateWorkIndex:1,

    onAddLocationSpecificLayerViewsToGeoMap: 1,
    onAddLocationSpecificAttributesToLayerView: 1,

    onUpdateEntitlements : 1,

    onTableDatasetLoaded : 1,
    onBufferComplete : 1,

    onCreateEvent: 1,
    onUpdateEvent: 1,
    onRemoveEvent: 1,

    onCreateSavedLocation: 1,
    onUpdateSavedLocation: 1,
    onRemoveSavedLocation: 1,

    onUpdateScheduleSettings: 1,
    onUpdateScheduleServices: 1,
    onUpdateScheduleShifts: 1,

    onCreateRecommendation: 1,
    onUpdateRecommendation: 1,
    onRemoveRecommendation: 1,
    onSetActiveRecommendation: 1,

    onUpdateLayerMetaAvailabilities: 1,
    onRemoveLayerMetaAvailabilities: 1,

    onGenerateSchedule: 1,

    onTerritoryEditStart : 1,
    onTerritoryEditFinish : 1
};

export default class API {
    private eventChannel;
    private rqBroker: AjaxRequestBroker;
    private cache: CacheModule;

    readonly addressSearchApi: AddressSearcherApi;
    readonly analysis: AnalysisModule;
    readonly appResources: AppResourcesModule;
    readonly appUserSupport: AppUserSupportModule;
    readonly asyncTasks: AsyncTasksModule;
    readonly bucket: BucketModule;
    readonly buffer: BufferModule;
    readonly dataStore: DataStoreModule;
    readonly distance: DistanceModule;
    readonly feature: FeatureModule;
    readonly geocode: GeocodeModule;
    readonly geom: GeomModule;
    readonly geoMap: GeoMapModule;
    readonly geoMapAnnotation: GeoMapAnnotationModule;
    readonly geoMapTemplate: GeoMapTemplate;
    readonly geoShape: GeoShapeModule;
    readonly heuristics: HeuristicsModule;
    readonly Import: ImportModule; // note: breaking convention with capital letter at start because 'import' is a reserved keyword
    readonly integrationSalesforce: IntegrationSalesforceModule;
    readonly layerMeta: LayerMetaModule;
    readonly layerView: LayerViewModule;
    readonly layerInfo: LayerInfoModule;
    readonly logging: LoggingModule;
    readonly notification: NotificationModule;
    readonly predefinedRanges: PredefinedRangesModule;
    readonly projection: ProjectionModule;
    readonly route: RouteModule;
    readonly schedule: ScheduleModule;
    readonly scheduledUpdate: ScheduledUpdateModule;
    readonly search: SearchModule;
    readonly security: SecurityModule;
    readonly status: StatusModule;
    readonly style: StyleModule;
    readonly symbolType: SymbolTypeModule;
    readonly tenant: TenantModule;
    readonly tenantAdmin: TenantAdminModule;
    readonly territory: TerritoryModule;
    readonly thematic: ThematicModule;
    readonly upload: UploadModule;
    readonly userSettings: UserSettingsModule;
    readonly webhook: WebhookModule;
    readonly webSocket: WebSocketModule | null;
    readonly workspace: WorkspaceModule;
    readonly export: ExportModule;

    readonly ai: AIModule;
    readonly mcp: MCPModule;

    constructor() {
        const eventChannel = es.util.createEventChannel(EVENTS, "es.api");

        $(window).on('beforeunload', function() {
            eventChannel.disableListeners();
            setTimeout(function(){
                eventChannel.enableListeners();
            }, 1000);
        });

        this.loadLocalStoragePreloadConfig();

        const rqBroker = new AjaxRequestBroker(eventChannel);
        const cache = new CacheModule();
        cache.initPreLoad();

        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.cache = cache;

        this.addressSearchApi = new AddressSearcherApi();
        this.analysis      = new AnalysisModule      (eventChannel, rqBroker, "Analysis", cache);
        this.appResources  = new AppResourcesModule  (eventChannel, rqBroker, cache);
        this.appUserSupport= new AppUserSupportModule(rqBroker, "app_user_support"); // no need for evts or cache
        this.asyncTasks    = new AsyncTasksModule    (eventChannel, rqBroker, "AsyncTasks", cache);
        this.bucket        = new BucketModule        (eventChannel, rqBroker, "Bucket", cache);
        this.buffer        = new BufferModule        (eventChannel, rqBroker, "buffer", cache);
        this.dataStore     = new DataStoreModule     (eventChannel, rqBroker, "DataStore", cache);
        this.distance      = new DistanceModule      (eventChannel, rqBroker, "distance", cache);
        this.feature       = new FeatureModule       (eventChannel, rqBroker, "Feature", cache);
        this.geocode       = new GeocodeModule       (eventChannel, rqBroker, "Geocode", cache);
        this.geom          = new GeomModule          (eventChannel, rqBroker, "Geom", cache);
        this.geoMap        = new GeoMapModule        (eventChannel, rqBroker, "GeoMap", cache);
        this.geoMapAnnotation = new GeoMapAnnotationModule(eventChannel, rqBroker, "GeoMapAnnotation", cache);
        this.geoMapTemplate = new GeoMapTemplate     (eventChannel, rqBroker, "geomap_template", cache);
        this.geoShape      = new GeoShapeModule      (eventChannel, rqBroker, "GeoShape", cache);
        this.heuristics    = new HeuristicsModule    (eventChannel, rqBroker, "Heuristics", cache);
        this.integrationSalesforce = new IntegrationSalesforceModule(eventChannel, rqBroker, "integration_salesforce", cache);
        this.Import        = new ImportModule        (eventChannel, rqBroker, "Import", cache);
        this.layerMeta     = new LayerMetaModule     (eventChannel, rqBroker, "LayerMeta", cache);
        this.layerView     = new LayerViewModule     (eventChannel, rqBroker, "LayerView", cache);
        this.layerInfo     = new LayerInfoModule     (eventChannel, rqBroker, cache);
        this.logging       = new LoggingModule       (eventChannel, rqBroker);
        this.notification  = new NotificationModule  (eventChannel, rqBroker, "Notification");
        this.predefinedRanges = new PredefinedRangesModule(eventChannel, rqBroker, "PredefinedRanges", cache);
        this.projection    = new ProjectionModule    (eventChannel, rqBroker, "Projection", cache);
        this.route         = new RouteModule         (eventChannel, rqBroker, "Route", cache);
        this.schedule      = new ScheduleModule      (eventChannel, rqBroker, "Schedule", cache);
        this.scheduledUpdate = new ScheduledUpdateModule(eventChannel, rqBroker, "scheduled_update", cache);
        this.search        = new SearchModule        (eventChannel, rqBroker, "Search", cache);
        this.security      = new SecurityModule      (eventChannel, rqBroker, "Security", cache);
        this.status        = new StatusModule        (eventChannel, rqBroker, "Status", cache);
        this.style         = new StyleModule         (eventChannel, rqBroker, "Style", cache);
        this.symbolType    = new SymbolTypeModule    (eventChannel, rqBroker, "SymbolType", cache);
        this.tenant        = new TenantModule        (eventChannel, rqBroker, "Tenant", cache);
        this.tenantAdmin   = new TenantAdminModule   (eventChannel, rqBroker);
        this.thematic      = new ThematicModule      (eventChannel, rqBroker, "Thematic", cache);
        this.userSettings  = new UserSettingsModule  (eventChannel, rqBroker, "UserSettings", cache);
        this.territory     = new TerritoryModule     (eventChannel, rqBroker, "Territory", cache);
        this.upload        = new UploadModule        (eventChannel, rqBroker, "Upload", cache);
        this.webhook       = new WebhookModule       (eventChannel, rqBroker, "webhook", cache);
        this.webSocket     = es.isPublicAccess ? null : new WebSocketModule(eventChannel, rqBroker, "WebSocket");
        this.workspace     = new WorkspaceModule     (eventChannel, rqBroker, "Workspace", cache);
        this.export        = new ExportModule        (eventChannel, rqBroker, "Export", cache);
        this.ai            = new AIModule            (eventChannel, rqBroker, "AI", cache);
        this.mcp           = new MCPModule           (rqBroker, "mcp");
    }

    /**
     * if es.loadLocalStoragePreloadConfig exists then load it up as appropriate.
     * This allows for setting of any localstorage property for all or specific tenants.
     * @private
     */
    private loadLocalStoragePreloadConfig() {
        if (es?.localStoragePreloadConfig) {
            es.log.info("loadLocalStoragePreloadConfig:", es.localStoragePreloadConfig);
            es.localStoragePreloadConfig.forEach(item => {
                const config = item.config;
                Object.entries(config).forEach(([key, val]) => {
                    es.log.info("loadLocalStoragePreloadConfig set:", key + "=" + val);
                    es.util.localStorage.set(key, val);
                });
            });
        }
    }

    /**
     * @function getRequestBroker
     * 
     * @return rqBroker
     */
    getRequestBroker(): AjaxRequestBroker {
        return this.rqBroker;
    }

    /**
     * May be necessary to access if dev has performed operations outside the client (e.g. created a sevelet that uses services) which would render clientside cache invalid
     * e.g. es.api._cache().getStats()
     */
    _cache(): CacheModule {
        return this.cache;
    }

    /**
     * @function setAuth
     *
     * Set Basic Auth which will automatically be used by subsequent API calls. This is useful in cases where a lot of API calls will be made and it 
     * would be inconvenient and/or verbose to pass in the auth opt param as the last param in each call - this is especially true with methods that 
     * have a number of optional params e.g:
     * <pre>
     * Method signature:
     *      es.api.geoMap.list(criteria, sortOrder, offset, length, opts);
     *
     * Minimal call:
     *      es.api.geoMap.list();
     *
     * Call with basic auth in api call:
     *      es.api.geoMap.list(undefined, undefined, undefined, undefined, {basicAuth: {email: email, password: password}});
     *
     * Call with basic auth with set auth:
     *      es.api.setAuth({basicAuth: {email: email, password: password}})
     *      es.api.geoMap.list();
     *      ...
     * </pre>
     * 
     * @param auth e.g. {basicAuth: {email: email, password: password}}
     */
    setAuth(auth) {
        this.cache.clearAll(); // need to clear all cache when a new user is set
        this.rqBroker.setAuth(auth);
    }

    /**
     * Get 'list' query string for url
     */
    getListQueryURL(resourceName: string, criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, entityId?: number, srid?: number, usedAfter?: number): string {
        let url = [es.api.getURL(resourceName)];
        if (entityId) {
            url.push("/", entityId);
        }
        
        let delimiter = "?";
        
        if (criteria) {
            url.push(delimiter, "criteria=", es.util.url.stringify(criteria));
            delimiter = "&";
        }
        
        if (sortOrder) {
            url.push(delimiter,"sortOrder=", es.util.url.stringify(sortOrder));
            delimiter = "&";
        }
        
        if (srid) {
            url.push(delimiter, "srid=", srid);
            delimiter = "&";
        }
        
        if (offset) {
            url.push(delimiter, "offset=", offset);
            delimiter = "&";
        }
        
        if (length) {
            url.push(delimiter, "length=", length);
            delimiter = "&";
        }

        if (usedAfter) {
            url.push(delimiter, "usedAfter=", usedAfter);
            //delimiter = "&";
        }
        
        return url.join("");
    }

    /**
     * Get get resource URL for resourceName
     */
    getURL(resourceName: string): string {
        return es.restUrl + "/" + resourceName.toLowerCase();
    }

    /**
     * @function es.api.getEventChannel
     * 
     * @return es.util.EventChannel instance
     */
    getEventChannel() {
        return this.eventChannel;
    }

    /**
     * Internal. Get request broker
     * @return {*}
     * @private
     */
    _getAjaxRequestBroker(): AjaxRequestBroker {
        return this.rqBroker;
    }
}
